import { Edge, Node } from "@xyflow/react";
import { ActionContextType, WorkflowStep, WorkflowStepGroup } from "../../../__generated__/api";
import {
    EDGE, ACTION, GROUP, nodeTypes,
    getGroupEdgeType,
    NODE_TYPE,
} from "./nodes";

export const ID_DELIMITER = "|";
export const SUBACTION_SUFFIX = "-subaction";

const INITIAL_NODES = [
    ACTION.REQUEST_CREATED,
    ACTION.REQUEST_CREATING,
    ACTION.GENERIC_NO_TICKET,
    ACTION.WEBHOOK,
];

function isInitialNode(nodeType: ACTION, parentId: string | null): boolean {
    return INITIAL_NODES.includes(nodeType) && parentId === null;
}

function buildLayout(
    step: WorkflowStep | WorkflowStepGroup,
    nodes: Node[],
    edges: Edge[],
    parentId: string | null,
) {
    if (step.__typename === "WorkflowStepGroup") {
        const groupId = `${step.groupId}${ID_DELIMITER}${parentId}`;

        nodes.push({
            id: groupId,
            position: { x: 0, y: 0 },
            type: step.groupId in nodeTypes ? step.groupId : GROUP.DEFAULT,
            data: {
                ...step,
                parentId,
            },
        });

        const edgeType = getGroupEdgeType(step.groupId);

        if (parentId) {
            edges.push({
                id: `edge-${parentId}-to-${groupId}`,
                source: parentId,
                target: groupId,
                type: edgeType,
                data: {
                    sourceId: parentId,
                    targetId: groupId,
                },
            });
        }

        const children = step.children ?? [];

        children.forEach(child => {
            buildLayout(child, nodes, edges, groupId);
        });
    }

    if (step.__typename === "WorkflowStep") {
        const nodeType = step.service
            ? step.service.name
            : step.actionId;

        nodes.push({
            id: step.id,
            position: { x: 0, y: 0 },
            data: {
                ...step,
                parentId,
            },
            type: nodeType in nodeTypes ? nodeType : NODE_TYPE.ACTION_BASE,
        });

        const parentNode = nodes.find(node => node.id === parentId);

        if (parentId && parentNode) {
            edges.push({
                id: `edge-${parentId}-to-${step.id}`,
                source: parentId,
                target: step.id,
                type: EDGE.CURVED,
                data: {
                    sourceId: parentId,
                    targetId: step.id,
                },
            });
        }

        let parent = step.id;
        const children = step.children ?? [];
        const allowedChildren = step?.allowedChildren ?? step?.action?.allowedChildren ?? [];

        const hasChildren = children.length > 0;
        const hasAllowedChildren = allowedChildren.length > 0;

        if ((hasChildren || hasAllowedChildren) && !isInitialNode(step.actionId, parentId) && nodeType !== ACTION.WEBHOOK) {
            const subactionId = `${step.id}${SUBACTION_SUFFIX}`;

            parent = subactionId;

            const label = nodeType === ACTION.CONDITION ? "Condition" : "Subaction";

            nodes.push({
                id: subactionId,
                position: { x: 0, y: 0 },
                type: nodeType === ACTION.CONDITION ? GROUP.CONDITION : GROUP.SUBACTION,
                data: {
                    ...step,
                    parentId: step.id,
                    label,
                    __typename: "WorkflowStepGroup",
                },
            });

            edges.push({
                id: `edge-${step.id}-to-${subactionId}`,
                source: step.id,
                target: subactionId,
                data: {
                    sourceId: step.id,
                    targetId: subactionId,
                },
            });
        }

        const childrenMap = new Map<string, WorkflowStepGroup>();
        children.forEach(child => {
            childrenMap.set(child.groupId, child);
        });

        allowedChildren.forEach(allowedChild => {
            const existingChild = childrenMap.get(allowedChild.id);
            const childNodeType: string = allowedChild.id;

            if (existingChild) {
                // Merge data from the allowed definition and recurse
                const mergedChild = {
                    ...existingChild,
                    label: allowedChild.label,
                    id: allowedChild.id,
                    output: allowedChild.output,
                    allowedActionIds: allowedChild.allowedActionIds,
                    description: allowedChild.description,
                    parentId: parent,
                    __typename: "WorkflowStepGroup" as const,
                };

                buildLayout(mergedChild, nodes, edges, parent);
            } else {
                const childNodeId = `${allowedChild.id}${ID_DELIMITER}${parent}`;

                nodes.push({
                    id: childNodeId,
                    position: { x: 0, y: 0 },
                    data: {
                        ...allowedChild,
                        parentId: parent,
                    },
                    type: childNodeType in nodeTypes ? childNodeType : GROUP.DEFAULT,
                });

                edges.push({
                    id: `edge-${parent}-to-${childNodeId}`,
                    source: parent,
                    target: childNodeId,
                    type: isInitialNode(step.actionId, parent) || nodeType === ACTION.WEBHOOK ? EDGE.STRAIGHT : EDGE.CURVED,
                    data: {
                        sourceId: parent,
                        targetId: childNodeId,
                    },
                });
            }
        });
    }
}

function createEmptyFlowNode(): Node {
    return {
        id: "empty-flow-node",
        type: NODE_TYPE.EMPTY_FLOW,
        data: {
            label: "Empty Flow Node",
        },
        position: { x: 0, y: 0 },
    };
}

export default function buildFlowFromWorkflows(workflowSteps?: WorkflowStep[] | null, actionsContextType?: ActionContextType) {
    const edges: Edge[] = [];
    const nodes: Node[] = [];

    let firstStep = workflowSteps?.[0];

    if (actionsContextType === ActionContextType.Request) {
        firstStep = workflowSteps?.find(step => step.actionId === ACTION.REQUEST_CREATING);
    } else if (actionsContextType === ActionContextType.Ticket) {
        firstStep = workflowSteps?.find(step => step.actionId === ACTION.REQUEST_CREATED);
    }

    if (!firstStep) {
        const node = createEmptyFlowNode();
        nodes.push(node);
        return { nodes, edges };
    }

    buildLayout(firstStep, nodes, edges, null);
    return { nodes, edges };
}
